import React, { useEffect, useRef, useState } from "react";
import { MdCancel } from "react-icons/md";
import { ReactSVG } from "react-svg";
import { useNavigate, useParams } from "react-router-dom";
import {
  formatFileSize,
  formatDateForInput,
  showErrorToast,
  formatDateTimeForInput,
} from "../../utils/Utils";
import ButtonLoading from "../Button loading/ButtonLoading";
import { truncateText } from "../Media/Media";
import { toast } from "react-toastify";
import { showConfirmDialog } from "../showConfirmDialog/ShowConfirmDialog";
import { useCreateAuctionMutation, useDeleteAuctionMutation, useGetAuctionByIdQuery, useUpdateAuctionMutation } from "../../redux/auctionApi";
import { useDeleteTenderMutation } from "../../redux/tenderApi";

const AddAuction = () => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [viewFile, setViewFile] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    auctionDate: "",
    alternativeDate: "",
  });

  const { id } = useParams();
  const isEditMode = Boolean(id);

  const { data, isLoading: isAuctionLoading } = useGetAuctionByIdQuery(id, {
    refetchOnFocus: false,
    skip: !isEditMode,
  });

  useEffect(() => {
    if (!isEditMode || !data?.auction) return;

    const { doc, title, auctionDate, alternativeDate } = data.auction;

    if (doc?.docUrl) {
      setFile({
        src: doc?.docUrl,
        fileType: doc?.fileType || "Unknown",
        fileSize: doc?.fileSize || "Unknown",
        fileName: doc?.fileName || "Unknown",
        file: null,
      });
    }

    setFormData({
      title: title || "",
      auctionDate: formatDateTimeForInput(auctionDate) || "",
     
      alternativeDate: formatDateTimeForInput(alternativeDate) || "",
    });
  }, [isEditMode, data]);

  const [createAuction, { isLoading: isCreateLoading }] =
    useCreateAuctionMutation();
  const [updateAuction, { isLoading: isUpdateLoading }] =
    useUpdateAuctionMutation();
  const [deleteAuction, { isLoading: isDeleteLoading }] =
    useDeleteAuctionMutation();
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };
  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = (file) => {
    setError("");

    if (!file) {
      setError("No file selected.");
      return;
    }

    const maxFileSize = 25 * 1024 * 1024; // 25MB
    if (file.size > maxFileSize) {
      setError("File size must be less than 25MB.");
      return;
    }

    const fileType = file.type;
    const objectUrl = URL.createObjectURL(file);

    if (fileType.startsWith("image/")) {
      const img = new Image();

      img.onload = () => {
        const newImage = {
          src: objectUrl,
          fileType: file.type,
          file,
        };
        setFile(newImage);
      };

      img.onerror = () => {
        setError("Invalid image file.");
        URL.revokeObjectURL(objectUrl);
      };

      img.src = objectUrl;
    } else {
      const newFile = {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        src: objectUrl,
        file,
      };

      setFile(newFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDelete = () => {
    setFile(null);
    setViewFile(false);
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    setError("");

    // TITLE
    if (!formData.title.trim()) {
      newErrors.title = "Title is required.";
      isValid = false;
    }

    // AUCTION DATE
    if (!formData.auctionDate.trim()) {
      newErrors.auctionDate = "Auction Date is required.";
      isValid = false;
    }

    if (formData.alternativeDate && formData.auctionDate) {
  const now = new Date();
  const auction = new Date(formData.auctionDate);

  const TWELVE_HOURS = 12 * 60 * 60 * 1000;
  const timeRemaining = auction.getTime() - now.getTime();

  if (timeRemaining > TWELVE_HOURS) {
    newErrors.alternativeDate =
      "Alternative Date can only be added after the Auction Date has passed or within 12 hours before the Auction Date.";
    isValid = false;
  }

  const alternative = new Date(formData.alternativeDate);

  if (auction.getTime() >= alternative.getTime()) {
    newErrors.alternativeDate =
      "Alternative Date must be later than the Auction Date.";
    isValid = false;
  }
}


    if (!isEditMode) {
      if (!file) {
        setError("Please upload a file.");
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    const auctionData = new FormData();
    auctionData.append("title", formData.title);
    auctionData.append("auctionDate", formData.auctionDate);
    auctionData.append("alternativeDate", formData.alternativeDate);
    auctionData.append("auctionDoc", file?.file);

    try {
      let response = null;
      if (isEditMode) {
        response = await updateAuction({ id, auctionData: auctionData }).unwrap();
      } else {
        response = await createAuction(auctionData).unwrap();
      }

      if (response.success) {
        handleCancel();
        toast.success(response.message);

        navigate("/auctions");
      }
    } catch (err) {
      showErrorToast(err);
    }
  };

  const handleCancel = () => {
    setFormData({
      title: "",
      auctionDate: "",
      alternativeDate: "",
    });
    setErrors({});
    setFile(null);
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDeleteAuction = async (id) => {
    try {
      const response = await deleteAuction(id).unwrap();
      if (response.success) {
        toast.success(response.message);

        navigate("/auctions");
      }
    } catch (err) {
      console.error("auction error:", err);
      showErrorToast(err);
    }
  };

  const handleDeleteConfrim = (id) => {
    showConfirmDialog({
      title: "Delete Auction",
      message: "Are you sure you want to delete this auction?",
      type: "danger",
      onConfirm: () => {
        handleDeleteAuction(id);
      },
    });
  };

  const auction = new Date(formData.auctionDate);
const now = new Date();
const TWELVE_HOURS = 12 * 60 * 60 * 1000;

const canAddAlternativeDate =
  auction.getTime() <= now.getTime() ||
  auction.getTime() - now.getTime() <= TWELVE_HOURS;
  return (
    <div className="flex flex-col gap-3 bg-white rounded-xl xl:p-8 lg:p-6 p-4 ">
      <h1 className="text-[#05004E] lg:text-[1.8rem] md:text-[1.4rem] text-[1.2rem] font-semibold">
        {isEditMode ? "Update" : "Add"} Auction
      </h1>
      {isAuctionLoading ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <div className="h-6 w-40 bg-[#efefef] animate-pulse rounded-lg"></div>
            <div className=" w-full h-12 rounded-xl bg-[#efefef] animate-pulse"></div>
          </div>
          <div className="flex flex-col gap-1 w-[50%]">
            <div className="h-6 w-40 bg-[#efefef] animate-pulse rounded-lg"></div>
            <div className=" w-full h-12 rounded-xl bg-[#efefef] animate-pulse"></div>
          </div>
          <div className="flex flex-col gap-1 ">
            <div className="h-6 w-40 bg-[#efefef] animate-pulse rounded-lg"></div>
            <div className=" w-full h-24 rounded-xl bg-[#efefef] animate-pulse"></div>
          </div>
          <div className="flex items-center justify-end gap-4">
            <div className="w-[10rem]  rounded-xl h-14 bg-[#efefef] animate-pulse"></div>
            <div className="w-[10rem]  rounded-xl h-14 bg-[#efefef] animate-pulse"></div>
            <div className="w-[10rem]  rounded-xl h-14 bg-[#efefef] animate-pulse"></div>
          </div>
        </div>
      ) : (
        <form
          className="flex flex-col lg:gap-4 md:gap-3 gap-2 lg:text-[1rem] text-[.9rem]"
          onSubmit={handleSubmit}
        >
          <div className="flex flex-col md:gap-1 gap-[2px] ">
            <label className="font-semibold">Title</label>
            <input
              type="text"
              name="title"
              placeholder="Enter Title"
              value={formData.title}
              onChange={handleChange}
              className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
            />
            {errors.title && (
              <p className="text-red-500 md:text-sm text-[12px]">
                {errors.title}
              </p>
            )}
          </div>
          <div className=" grid sm:grid-cols-2 gap-5">
            <div className="flex flex-col md:gap-1 gap-[2px] flex-1 w-full">
              <label className="font-semibold">Auction Date</label>
              <input
                type="datetime-local"
                name="auctionDate"
                placeholder="March 26,2025"
                value={formData.auctionDate}
                onChange={handleChange}
                min={new Date().toISOString().slice(0, 16)}
                className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
              />
              {errors.auctionDate && (
                <p className="text-red-500 md:text-sm text-[12px] ">
                  {errors.auctionDate}
                </p>
              )}
            </div>
            { isEditMode && canAddAlternativeDate && (


            <div className="flex flex-col md:gap-1 gap-[2px] flex-1 w-full">
              <label className="font-semibold">Alternative Date</label>
              <input
                type="datetime-local"
                name="alternativeDate"
                placeholder="March 26,2025"
                value={formData.alternativeDate}
                onChange={handleChange}
                disabled={canAddAlternativeDate}
                className="w-full border border-[#00000033] text-[#737791] outline-none lg:p-4 md:p-3 p-2 lg:rounded-xl rounded-lg"
              />
              {errors.alternativeDate && (
                <p className="text-red-500 md:text-sm text-[12px] ">
                  {errors.alternativeDate}
                </p>
              )}
            </div>
            )}
          </div>


          <div className="flex flex-col md:gap-1 gap-[2px] ">
            <div className="font-semibold ">Upload Document</div>
            <div
              className="w-full border border-dashed border-[#00000033] md:p-4 p-3 rounded-xl "
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <label
                htmlFor="file-upload"
                className="cursor-pointer  flex flex-col md:gap-2 gap-1 md:py-4 py-2 items-center w-full "
              >
                <div className="md:p-3 p-1.5 bg-[#02643B20] rounded-full">
                  <ReactSVG
                    src={"/icon/upload file.svg"}
                    className={`lg:size-10 md:size-8 size-6`}
                  />
                </div>
                <p className="text-center font-semibold text-[#0B0B0B] ">
                  Drop your files here or{" "}
                  <span className=" text-[#00643b]  ">Click to upload</span>
                </p>

                <p className="text-[#6D6D6D] text-center md:text-sm text-[12px] ">
                  SVG, PNG, JPG, GIF or PDF etc.
                </p>
                <p className="text-[#6D6D6D] md:text-sm text-[12px] text-center">
                  Max file size is 25Mb
                </p>
              </label>

              <input
                id="file-upload"
                type="file"
                accept="
  image/*,
  application/pdf,
  application/msword,
  application/vnd.openxmlformats-officedocument.wordprocessingml.document,
  application/vnd.ms-powerpoint,
  application/vnd.openxmlformats-officedocument.presentationml.presentation,
  application/vnd.ms-excel,
  application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
"
                onChange={(e) => handleFile(e.target.files[0])}
                className="hidden"
                ref={fileInputRef}
              />
              {file && (
                <div className="flex justify-center text-[#6D6D6D] items-center md:gap-4 gap-3 md:text-sm text-[12px] lg:py-[1.3rem] md:py-[1rem] py-[.5rem]">
                  <hr className="border-[#00000033] md:w-[22.22%] w-[30%] border-1" />
                  OR
                  <hr className="border-[#00000033] md:w-[22.22%] w-[30%] border-1" />
                </div>
              )}

              {file &&
                (file?.src && file?.fileType.startsWith("image/") ? (
                  <div className="relative">
                    <img
                      src={file.src}
                      alt="Upload Preview"
                      className="object-cover w-full md:rounded-[1rem] rounded-lg"
                    />
                    <button
                      onClick={handleDelete}
                      className="absolute md:-top-2.5 md:-right-2.5 -top-1.5 -right-1.5 bg-white rounded-full  "
                    >
                      <MdCancel className="lg:size-10 md:size-8 size-6 text-red-500" />
                    </button>
                  </div>
                ) : file?.fileType === "application/pdf" ? (
                  <>
                    <div className="flex items-center justify-between gap-2 lg:p-4 md:p-3 p-2 border rounded-lg ">
                      <div className="flex items-center lg:gap-4 md:gap-3 gap-2 ">
                        <ReactSVG
                          src={"/icon/file.svg"}
                          className={`lg:size-10 md:size-8 size-6`}
                        />

                        <div>
                          <p
                            title={file.fileName}
                            className="font-medium lg:text-sm md:text-[12px] text-[10px] capitalize "
                          >
                            {truncateText(file.fileName, 15)}
                          </p>
                          <p className="lg:text-sm md:text-[12px] text-[10px]">
                            {formatFileSize(file.fileSize)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div
                          className=" md:flex hidden cursor-pointer text-[#00643b] font-medium"
                          onClick={() => setViewFile(!viewFile)}
                        >
                          Click to {viewFile ? "close" : "view"}
                        </div>
                        <div
                          onClick={handleDelete}
                          className=" text-red-500 font-medium bg-[#66666620] md:p-2 py-1 px-2  md:text-[1rem] text-[.85rem]  rounded-md cursor-pointer"
                        >
                          Remove
                        </div>
                      </div>
                    </div>
                    {viewFile && (
                      <iframe
                        src={file.src}
                        width="100%"
                        height="500px"
                        className="rounded-[1rem] mt-2"
                        title="File Preview"
                      />
                    )}
                  </>
                ) : (
                  <div className="flex items-center justify-between gap-2 lg:p-4 md:p-3 p-2 border rounded-lg ">
                    <div className="flex items-center lg:gap-4 md:gap-3 gap-2  max-w-full">
                      <ReactSVG
                        src={"/icon/file.svg"}
                        className={`lg:size-10 md:size-8 size-6 `}
                      />

                      <div>
                        <p
                          title={file.fileName}
                          className="font-medium lg:text-sm md:text-[12px] text-[10px] capitalize  "
                        >
                          {truncateText(file.fileName, 15)}
                        </p>
                        <p className="lg:text-sm md:text-[12px] text-[10px]">
                          {formatFileSize(file.fileSize)}
                        </p>
                      </div>
                    </div>
                    <div
                      onClick={handleDelete}
                      className=" text-red-500 font-medium bg-[#66666620] md:p-2 py-1 px-2  md:text-[1rem] text-[.85rem]  rounded-md cursor-pointer"
                    >
                      Remove
                    </div>
                  </div>
                ))}
            </div>
            {error && (
              <p className="text-red-500 md:text-sm text-[12px]">{error}</p>
            )}
          </div>

          <div className="flex items-center justify-end lg:gap-4 md:gap-3 gap-2">
            <p
              onClick={() => handleCancel()}
              className="lg:w-[10rem] md:w-[9rem] w-max md:py-2 md:px-0 py-1 px-2 md:text-[1rem] text-[.85rem]  border  border-[#00643B] text-[#00643B] text-center cursor-pointer  lg:rounded-xl md:rounded-lg rounded-md font-medium"
            >
              Cancel
            </p>
            {data || isEditMode ? (
              <div
                disabled={isDeleteLoading ? true : false}
                onClick={() => handleDeleteConfrim(data?.auction?._id)}
                className={`lg:w-[10rem] md:w-[9rem] w-max md:py-2 md:px-0 py-1 px-2 md:text-[1rem] text-[.85rem]  border text-center border-[#D80000] flex items-center justify-center ${
                  isDeleteLoading
                    ? "opacity-50 cursor-not-allowed"
                    : "opacity-100 cursor-pointer"
                } text-white bg-[#D80000] lg:rounded-xl md:rounded-lg rounded-md font-medium`}
              >
                {isDeleteLoading ? (
                  <ButtonLoading text=" Delete..." />
                ) : (
                  "Delete"
                )}
              </div>
            ) : (
              ""
            )}
            <button
              type="submit"
              disabled={isCreateLoading || isUpdateLoading ? true : false}
              className="lg:w-[10rem] md:w-[9rem] w-max md:py-2 md:px-0 py-1 px-2 md:text-[1rem] text-[.85rem]  border border-[#00643B] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-white bg-[#00643B] lg:rounded-xl md:rounded-lg rounded-md font-medium"
            >
              {isEditMode ? (
                isUpdateLoading ? (
                  <ButtonLoading text="Update..." />
                ) : (
                  "Update"
                )
              ) : isCreateLoading ? (
                <ButtonLoading text="Add..." />
              ) : (
                "Add"
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AddAuction;
